import type WaCombobox from '@awesome.me/webawesome-pro/dist/components/combobox/combobox.js'
import type WaOption from '@awesome.me/webawesome-pro/dist/components/option/option.js'
import type WaTooltip from '@awesome.me/webawesome-pro/dist/components/tooltip/tooltip.js'
import * as Sentry from '@sentry/browser'
import {
  hasProp,
  isBoolean,
  isDeepEqual,
  isPlainObject,
  isString,
  uniqueBy
} from 'remeda'
import { type Cleanup, composeCleanup } from '../../helpers/cleanup'
import { isColor } from '../../helpers/colors'
import { iconUrl } from '../../helpers/icon'
import {
  bindLiveProps,
  type JsonObject,
  type JsonValue,
  type LivePropValues,
  readRenderedLiveProp
} from '../../helpers/live-props'
import { triggerForm, type ViewHook } from '../../helpers/utils'
import {
  addRemoteOption,
  buildOptionPagePayload,
  type ComboboxOption,
  createRemoteLoadState,
  type Decoration,
  failRemoteLoad,
  isDecoration,
  mergeRemoteBootstrapOptions,
  type OptionPageRequest,
  parseComboboxOptions,
  parseOptionPageReply,
  parseStringValues,
  receiveRemotePage,
  remoteOptions,
  remoteOptionsAreCurrent,
  remoteOptionsContain,
  replaceRemoteBootstrapOptions,
  requestIsCurrent,
  startNextRemotePage,
  startRemoteQuery
} from './model'
import {
  clientEventHook,
  DEFAULT_PUSH_EVENT_TIMEOUT_MS,
  isAbortError,
  pushEventWithCallback
} from './utils'

// Private types ---------------------------------------------------------------

type Carrier = HTMLInputElement | HTMLSelectElement

type CanonicalPatch = Readonly<{
  replaceInputDraft: boolean
  values: readonly string[] | undefined
}>

type ComboboxElements = Readonly<{
  announcer: HTMLElement
  carrier: Carrier
  clearIcon: HTMLElement
  combobox: WaCombobox
  helper: HTMLElement
  hint: HTMLElement
  prefix: HTMLElement
  spinner: HTMLElement
  status: HTMLElement
}>

type InputRestoration = 'none' | 'empty-draft' | 'draft'

type IconLoading = 'eager' | 'visible'

type StatusKind = 'normal' | 'failure'

type ComboboxConfiguration = Readonly<{
  clientOptions: boolean
  createEvent?: string
  createInDrawer: boolean
  dataLoadEvent?: string
  debounceMs?: number
  failureMessage: string
  loadingMessage: string
  noResultsMessage: string
  target?: string
}>

type ServerState = {
  autofocus: boolean
  clearable: boolean
  decoration: Decoration
  disabled: boolean
  errorMessage: string
  helperText: string
  invalid: boolean
  label: string
  loadParams: JsonObject
  multiple: boolean
  options: readonly ComboboxOption[]
  placeholder: string
  readonly: boolean
  required: boolean
  size: string
}

type ComboboxController = Readonly<{
  cleanup: Cleanup
  disconnected: () => void
  reconnected: () => void
  sync: () => void
}>

type ControllerHook = ViewHook & {
  readonly el: HTMLElement
}

type TagTooltipPresentation =
  | Readonly<{ content: string; kind: 'label' }>
  | Readonly<{ content: string; kind: 'overflow'; label: string }>

type TagFitRange = Readonly<{
  best: number
  lower: number
  upper: number
}>

// Constants -------------------------------------------------------------------

const FORM_UPDATE_DEBOUNCE_MS = 200
const EAGER_OPTION_ICON_COUNT = 24
const INITIAL_REMOTE_POPUP_DELAY_MS = 150
const INACTIVE_HINT_SLOT = 'combobox-feedback'
const LOADING_PRESENTATION_DELAY_MS = 150
const OPTION_ICON_PRELOAD_MARGIN_PX = 100
const OPTION_TOKEN_SPACE_PATTERN = /\s/
const POPUP_DISTANCE_PX = 4
const POPUP_VIEWPORT_PADDING_PX = 16
const TAG_LAYOUT_OVERFLOW_TOLERANCE_PX = 1
const VALID_SIZES: ReadonlySet<string> = new Set(['xs', 's', 'm', 'l', 'xl'])

const isComboboxSize = (value: string): value is WaCombobox['size'] =>
  VALID_SIZES.has(value)

// Private functions -----------------------------------------------------------

// Registers one listener and preserves its identity for teardown.
const listen = (
  target: EventTarget,
  type: string,
  listener: EventListener,
  options?: boolean | AddEventListenerOptions
): Cleanup => {
  target.addEventListener(type, listener, options)
  return () => target.removeEventListener(type, listener, options)
}

// WA popups restart on any slotchange bubbling through their anchor, so a nested
// slot appearing (the clear icon on the first pick) would hide the open list and
// reset its scroll. The popup's own anchor slot change never passes through here.
export const isolateAnchorSlotChanges = (anchor: HTMLElement): Cleanup =>
  listen(anchor, 'slotchange', (event) => event.stopPropagation())

// Keeps WA option filtering from hiding content in named slots.
const preserveSlotVisibility = (element: HTMLElement): Cleanup => {
  const restoreVisibility = (): void => element.removeAttribute('hidden')
  const observer = new window.MutationObserver(restoreVisibility)
  restoreVisibility()
  observer.observe(element, {
    attributeFilter: ['hidden'],
    attributes: true
  })

  return () => observer.disconnect()
}

// Keeps WA filtering from overriding Facade's conditional start-slot state.
const preserveSelectedPrefixVisibility = (prefix: HTMLElement): Cleanup => {
  const restoreVisibility = (): void => {
    const hidden = !prefix.hasAttribute('data-visible')
    if (prefix.hidden !== hidden) {
      prefix.hidden = hidden
    }
  }
  const observer = new window.MutationObserver(restoreVisibility)
  restoreVisibility()
  observer.observe(prefix, {
    attributeFilter: ['hidden'],
    attributes: true
  })
  return () => observer.disconnect()
}

// Identifies Web Awesome's shadow-DOM clear action without inspecting internals.
const comesFromClearButton = (event: Event): boolean =>
  event
    .composedPath()
    .some(
      (target) =>
        target instanceof window.Element &&
        target.matches('[part~="clear-button"]')
    )

// Identifies option interactions that should retain WA's native selection flow.
const comesFromOption = (event: Event): boolean =>
  event
    .composedPath()
    .some(
      (target) =>
        target instanceof window.Element && target.matches('wa-option')
    )

// Identifies one native input gesture through the shadow-DOM composed path.
const comesFromComboboxInput = (
  event: Event,
  input: HTMLInputElement
): boolean => event.composedPath()[0] === input

// Accepts only the leading pointer gesture that owns native caret placement.
const isPrimaryPointerGesture = (event: Event): event is PointerEvent =>
  event instanceof window.PointerEvent && event.isPrimary && event.button === 0

// Preserves the only non-editing character shortcut supported by readonly inputs.
const isUnmodifiedCopyShortcut = (event: KeyboardEvent): boolean =>
  event.key.toLowerCase() === 'c' &&
  event.ctrlKey !== event.metaKey &&
  !event.altKey &&
  !event.shiftKey

// Supplies localized empty feedback to WA's shared visual and spoken status.
const emptyStatusElement = (
  slot: 'empty' | 'no-results',
  message: string
): HTMLElement => {
  const element = document.createElement('span')
  element.slot = slot
  element.textContent = message
  return element
}

// Identifies WA's native custom-creation option.
const isCreateOption = (option: WaOption): boolean =>
  option.hasAttribute('data-create-option')

// Checks the option visibility WA uses to decide whether a popup can open.
const hasVisibleSelectableOption = (combobox: WaCombobox): boolean =>
  Array.from(combobox.querySelectorAll('wa-option')).some(
    (option) => !(option.hidden || option.disabled)
  )

// Narrows one hook child to a supported native form carrier.
const isCarrier = (element: Element | null): element is Carrier =>
  element instanceof window.HTMLInputElement ||
  element instanceof window.HTMLSelectElement

// Finds every required element through stable IDs and local ownership.
const findElements = (root: HTMLElement): ComboboxElements | undefined => {
  const announcer = root.querySelector('[data-part="status-announcer"]')
  const combobox = root.querySelector('wa-combobox')
  const carrier = root.querySelector(
    `#${CSS.escape(root.dataset.comboboxId ?? '')}`
  )
  const clearIcon = root.querySelector('[slot="clear-icon"]')
  const helper = root.querySelector('[data-part="helper-text"]')
  const hint = root.querySelector('[data-part="hint"]')
  const prefix = root.querySelector('[data-part="selected-prefix"]')
  const spinner = root.querySelector('[data-part="loading-indicator"]')
  const status = root.querySelector('[data-part="status"]')

  return announcer instanceof HTMLElement &&
    combobox &&
    isCarrier(carrier) &&
    clearIcon instanceof HTMLElement &&
    helper instanceof HTMLElement &&
    hint instanceof HTMLElement &&
    prefix instanceof HTMLElement &&
    spinner instanceof HTMLElement &&
    status instanceof HTMLElement
    ? {
        announcer,
        carrier,
        clearIcon,
        combobox,
        helper,
        hint,
        prefix,
        spinner,
        status
      }
    : undefined
}

// Reads a non-negative numeric debounce and ignores Phoenix's blur mode.
const debounceFrom = (element: HTMLElement): number | undefined => {
  const raw = element.getAttribute('phx-debounce')

  if (!raw || raw === 'blur') {
    return undefined
  }

  const value = Number.parseInt(raw, 10)
  return Number.isFinite(value) && value >= 0 ? value : undefined
}

// Honors a declared `phx-debounce` for form changes; otherwise batches multi-select picks.
export const formUpdateDelay = (
  debounceMs: number | undefined,
  multiple: boolean
): number => debounceMs ?? (multiple ? FORM_UPDATE_DEBOUNCE_MS : 0)

// Resolves the LiveView target from the explicit component or enclosing form.
const targetFrom = (root: HTMLElement): string | undefined =>
  root.dataset.target ||
  root.getAttribute('phx-target') ||
  root.closest('form')?.getAttribute('phx-target') ||
  undefined

// Builds immutable runtime configuration from server-rendered metadata.
const configurationFrom = (root: HTMLElement): ComboboxConfiguration => ({
  clientOptions: root.hasAttribute('data-client-options'),
  createEvent: root.dataset.eventCreate,
  createInDrawer: root.hasAttribute('data-create-in-drawer'),
  dataLoadEvent: root.dataset.eventDataLoad,
  debounceMs: debounceFrom(root),
  failureMessage:
    root.dataset.loadErrorMessage ?? 'Failed to load items. Please try again.',
  loadingMessage: root.dataset.loadingMessage ?? 'Loading',
  noResultsMessage: root.dataset.noResultsMessage ?? 'No results found',
  target: targetFrom(root)
})

// Reads canonical selected values from the native form carrier.
const carrierValues = (carrier: Carrier): readonly string[] =>
  carrier instanceof window.HTMLSelectElement
    ? Array.from(carrier.selectedOptions, (option) => option.value).filter(
        (value) => value !== ''
      )
    : carrier.value === ''
      ? []
      : [carrier.value]

// Converts one option into JSON-safe live-prop data.
const optionLiveValue = (option: ComboboxOption): JsonObject => ({
  label: option.label,
  value: option.value,
  ...(option.disabled === undefined ? {} : { disabled: option.disabled }),
  ...(option.icon === undefined ? {} : { icon: option.icon }),
  ...(option.color === undefined ? {} : { color: option.color })
})

// Treats a parsed live-prop object as the JSON object it already is.
const isJsonObject = (value: JsonValue | undefined): value is JsonObject =>
  isPlainObject(value)

// Reads initial canonical options from server-rendered option elements.
const optionsFromDom = (combobox: WaCombobox): readonly ComboboxOption[] =>
  uniqueBy(
    Array.from(combobox.querySelectorAll('wa-option')).flatMap((option) => {
      const value = option.dataset.value
      const label = option.label

      return value && label
        ? [
            {
              label,
              value,
              ...(option.disabled ? { disabled: true } : {}),
              ...(option.dataset.icon ? { icon: option.dataset.icon } : {}),
              ...(option.dataset.color ? { color: option.dataset.color } : {})
            }
          ]
        : []
    }),
    (option) => option.value
  )

// Initializes patchable server state from the rendered control.
const serverStateFrom = (elements: ComboboxElements): ServerState => ({
  autofocus: false,
  clearable: elements.combobox.withClear,
  decoration: isDecoration(elements.combobox.dataset.decoration)
    ? elements.combobox.dataset.decoration
    : 'none',
  disabled: elements.combobox.disabled,
  errorMessage: '',
  helperText: elements.helper.textContent ?? '',
  invalid: elements.combobox.getAttribute('aria-invalid') === 'true',
  label: elements.combobox.label,
  loadParams: {},
  multiple: elements.combobox.multiple,
  options: optionsFromDom(elements.combobox),
  placeholder: elements.combobox.placeholder,
  readonly: elements.combobox.hasAttribute('readonly'),
  required: elements.combobox.required,
  size: elements.combobox.size
})

// Retains the current boolean when a live value fails boundary validation.
const liveBoolean = (
  value: JsonValue | undefined,
  current: boolean
): boolean => (isBoolean(value) ? value : current)

// Retains the current string when a live value fails boundary validation.
const liveString = (value: JsonValue | undefined, current: string): string =>
  isString(value) ? value : current

// Lets external server changes replace an inactive draft without interrupting focused edits.
const canonicalValuesForPatch = (
  root: HTMLElement,
  reconciledValue: JsonValue | undefined
): CanonicalPatch => {
  const reconciled = parseStringValues(reconciledValue)
  const rendered = parseStringValues(
    readRenderedLiveProp(root, 'draft', 'values')
  )
  const replaceInputDraft =
    !root.matches(':focus-within') &&
    rendered !== undefined &&
    !isDeepEqual(rendered, reconciled)

  return {
    replaceInputDraft,
    values: replaceInputDraft ? rendered : reconciled
  }
}

// Merges validated live values into one complete server-owned state.
const mergeServerState = (
  current: ServerState,
  values: LivePropValues,
  options: readonly ComboboxOption[] | undefined,
  loadParams: JsonObject | undefined,
  decoration: Decoration | undefined
): ServerState => ({
  autofocus: liveBoolean(values.autofocus, current.autofocus),
  clearable: liveBoolean(values.clearable, current.clearable),
  decoration: decoration ?? current.decoration,
  disabled: liveBoolean(values.disabled, current.disabled),
  errorMessage: liveString(values.errorMessage, current.errorMessage),
  helperText: liveString(values.helperText, current.helperText),
  invalid: liveBoolean(values.invalid, current.invalid),
  label: liveString(values.label, current.label),
  loadParams: loadParams ?? current.loadParams,
  multiple: liveBoolean(values.multiple, current.multiple),
  options: options ?? current.options,
  placeholder: liveString(values.placeholder, current.placeholder),
  readonly: liveBoolean(values.readonly, current.readonly),
  required: liveBoolean(values.required, current.required),
  size: liveString(values.size, current.size)
})

// Appends canonical values to a multi carrier or replaces a single value.
const writeCarrier = (
  carrier: Carrier,
  values: readonly string[],
  disabled: boolean,
  target?: string
): void => {
  carrier.disabled = disabled

  if (target) {
    carrier.setAttribute('phx-target', target)
  } else {
    carrier.removeAttribute('phx-target')
  }

  if (carrier instanceof window.HTMLInputElement) {
    const value = values[0] ?? ''
    carrier.value = value
    carrier.setAttribute('value', value)
    return
  }

  const selectedOptions = (values.length === 0 ? [''] : values).map((value) => {
    const option = document.createElement('option')
    option.value = value
    option.textContent = value
    option.defaultSelected = true
    option.selected = true
    return option
  })

  carrier.replaceChildren(...selectedOptions)
}

// Adds validated palette classes to one decorated element.
const applyPalette = (
  element: HTMLElement,
  color: string | undefined
): void => {
  if (isColor(color)) {
    element.style.setProperty(
      '--combobox-decoration-background',
      `var(--color-${color}-3)`
    )
    element.style.setProperty(
      '--combobox-decoration-border',
      `var(--color-${color}-5)`
    )
    element.style.setProperty(
      '--combobox-decoration-color',
      `var(--color-${color}-11)`
    )
    element.style.setProperty(
      '--combobox-decoration-swatch',
      `var(--color-${color}-8)`
    )
  }
}

// Builds a decorative icon using the trusted Phosphor endpoint.
const iconDecoration = (
  name: string,
  loading: IconLoading = 'eager'
): HTMLElement => {
  const icon = document.createElement('wa-icon')
  const src = iconUrl(name)
  if (loading === 'eager') {
    icon.setAttribute('src', src)
  } else {
    icon.dataset.deferredSrc = src
  }
  icon.setAttribute('aria-hidden', 'true')
  icon.className = 'combobox-decoration__icon'
  return icon
}

// Builds one swatch whose palette is supplied by a validated color token.
const swatchDecoration = (color: string): HTMLElement => {
  const swatch = document.createElement('span')
  swatch.className = 'combobox-decoration__swatch'
  swatch.setAttribute('aria-hidden', 'true')
  applyPalette(swatch, color)
  return swatch
}

// Chooses the leading visual for one option without injecting HTML.
const leadingDecoration = (
  option: ComboboxOption,
  decoration: Decoration,
  iconLoading: IconLoading = 'eager'
): HTMLElement | undefined => {
  if ((decoration === 'icon' || decoration === 'badge') && option.icon) {
    return iconDecoration(option.icon, iconLoading)
  }

  return (decoration === 'color' || decoration === 'badge') &&
    isColor(option.color)
    ? swatchDecoration(option.color)
    : undefined
}

// Builds the visible row content for one option.
const optionContent = (
  option: ComboboxOption,
  decoration: Decoration,
  iconLoading: IconLoading = 'eager'
): readonly HTMLElement[] => {
  const label = document.createElement('span')
  label.className = 'combobox-option__label'
  label.textContent = option.label

  if (decoration === 'badge') {
    const badge = document.createElement('span')
    badge.className = 'combobox-decoration combobox-decoration--badge'
    applyPalette(badge, option.color)
    const leading = leadingDecoration(option, decoration, iconLoading)
    badge.append(...(leading ? [leading, label] : [label]))
    return [badge]
  }

  const leading = leadingDecoration(option, decoration, iconLoading)
  if (leading) {
    leading.setAttribute('slot', 'start')
  }

  return leading ? [leading, label] : [label]
}

// Builds one WA option with an opaque, space-safe control value.
const optionElement = (
  option: ComboboxOption,
  token: string,
  decoration: Decoration,
  iconLoading: IconLoading
): WaOption => {
  const element = document.createElement('wa-option')
  element.value = token
  element.label = option.label
  element.disabled = option.disabled === true
  element.dataset.value = option.value
  element.dataset.icon = option.icon ?? ''
  element.dataset.color = option.color ?? ''
  element.append(...optionContent(option, decoration, iconLoading))
  return element
}

// Rebinds WA's public navigation state after client-owned options are replaced.
const synchronizeOptionNavigation = (
  combobox: WaCombobox,
  allOptions: readonly WaOption[],
  navigableOptions: readonly WaOption[],
  previousCurrentValue: string | undefined
): void => {
  const selectableOptions = navigableOptions.filter(
    (option) => !option.disabled
  )
  const currentOption =
    selectableOptions.find(
      (option) => option.dataset.value === previousCurrentValue
    ) ?? selectableOptions[0]

  combobox.filteredOptions = selectableOptions
  for (const option of allOptions) {
    option.current = option === currentOption
    option.tabIndex = option === currentOption ? 0 : -1
  }

  combobox.currentOption = currentOption
}

// Uses the pinned WA announcer after Facade replaces remote options externally.
const announceFilterResults = (combobox: WaCombobox): void => {
  const announce: unknown = Reflect.get(combobox, 'announceFilterResults')
  if (typeof announce !== 'function') {
    throw new TypeError('Web Awesome combobox result announcer is unavailable')
  }

  Reflect.apply(announce, combobox, [])
}

// Creates the WA tag used for one selected multi-value.
const tagElement = (
  option: ComboboxOption,
  token: string,
  decoration: Decoration
): HTMLElement => {
  const tag = document.createElement('wa-tag')
  tag.setAttribute('with-remove', '')
  tag.setAttribute('appearance', 'filled')
  tag.setAttribute('variant', 'brand')
  tag.setAttribute('size', 'xs')
  tag.setAttribute('part', 'tag combobox-tag')
  tag.setAttribute(
    'exportparts',
    'content:tag__content, remove-button:tag__remove-button, remove-button__base:tag__remove-button__base'
  )
  tag.dataset.value = token

  if (decoration === 'badge' && isColor(option.color)) {
    tag.setAttribute('pill', '')
    tag.setAttribute('part', 'tag combobox-tag combobox-tag-decorated')
    tag.style.setProperty(
      '--combobox-tag-background',
      `var(--color-${option.color}-3)`
    )
    tag.style.setProperty(
      '--combobox-tag-border',
      `var(--color-${option.color}-5)`
    )
    tag.style.setProperty(
      '--combobox-tag-color',
      `var(--color-${option.color}-11)`
    )
  }

  const leading = leadingDecoration(option, decoration)
  if (leading) {
    leading.setAttribute('part', 'combobox-tag-icon')
  }

  const label = document.createElement('span')
  label.setAttribute('part', 'combobox-tag-label')
  label.textContent = option.label
  tag.append(...(leading ? [leading, label] : [label]))

  return tag
}

// Builds one DOM-safe tooltip in the same shadow root as its current tag.
const tagTooltipElement = (anchorId: string, comboboxId: string): WaTooltip => {
  const tooltip = document.createElement('wa-tooltip')
  tooltip.id = `${anchorId}-tooltip`
  tooltip.setAttribute('for', anchorId)
  tooltip.placement = 'top'
  tooltip.trigger = 'manual'
  tooltip.setAttribute('data-facade-fallback', 'overlay')
  tooltip.dataset.theme = 'dark'
  tooltip.style.setProperty('--max-width', 'var(--facade-tooltip-max-width)')
  tooltip.dataset.part = 'tag-tooltip'
  tooltip.dataset.anchorId = anchorId
  tooltip.dataset.comboboxId = comboboxId
  // Prevents nested overlay events from toggling the combobox popup.
  const stopLifecyclePropagation = (event: Event): void =>
    event.stopPropagation()
  tooltip.addEventListener('wa-show', stopLifecyclePropagation)
  tooltip.addEventListener('wa-after-show', stopLifecyclePropagation)
  tooltip.addEventListener('wa-hide', stopLifecyclePropagation)
  tooltip.addEventListener('wa-after-hide', stopLifecyclePropagation)
  return tooltip
}

// Encodes arbitrary canonical values as deterministic, space-safe DOM ID data.
const domIdToken = (value: string): string =>
  Array.from(
    value,
    (character) => character.codePointAt(0)?.toString(16) ?? ''
  ).join('-')

// Reports one operational failure without moving effects into pure code.
const captureFailure = (
  error: unknown,
  operation: string,
  root: HTMLElement,
  extra: Readonly<Record<string, unknown>> = {}
): void => {
  Sentry.captureException(error, {
    tags: { component: 'combobox', operation },
    extra: { comboboxId: root.dataset.comboboxId, ...extra }
  })
}

// Configures all behavior for one Web Awesome combobox.
const setupCombobox = (
  root: HTMLElement,
  hook: ControllerHook
): ComboboxController | undefined => {
  const elements = findElements(root)
  if (!elements) {
    captureFailure(new Error('Combobox markup is incomplete'), 'mounted', root)
    return undefined
  }

  const {
    announcer,
    carrier,
    clearIcon,
    combobox,
    helper,
    hint,
    prefix,
    spinner,
    status
  } = elements
  const currentComboboxInput = (): HTMLInputElement | undefined => {
    const shadowRoot = combobox.shadowRoot
    if (shadowRoot) {
      const renderedInput = shadowRoot.querySelector('[part~="combobox-input"]')
      return renderedInput instanceof window.HTMLInputElement
        ? renderedInput
        : undefined
    }

    const input: unknown = combobox.comboboxInput
    return input instanceof window.HTMLInputElement ? input : undefined
  }
  const readInputValue = (): string =>
    isString(combobox.inputValue) ? combobox.inputValue : ''
  const configuration = configurationFrom(root)
  const optionsHook = configuration.clientOptions ? clientEventHook(root) : hook
  const emptyStatusSlots = [
    emptyStatusElement('empty', configuration.noResultsMessage),
    emptyStatusElement('no-results', configuration.noResultsMessage)
  ]
  combobox.append(...emptyStatusSlots)
  let server = serverStateFrom(elements)
  let selectedValues = carrierValues(carrier)
  let staticOptions = server.options
  let remoteState = createRemoteLoadState(server.options)
  let active = true
  let connected = true
  let creating = false
  let creationRevision = 0
  let creationFailure: string | undefined
  let connectionAbortController = new AbortController()
  let wantsOpen = false
  let inputPopupOpenPending = false
  let autofocusApplied = false
  let applyingControlState = false
  let controlWriteRevision = 0
  let inputDraft = readInputValue()
  let inputRevision = 0
  let singleDraftClearedSelection = false
  let pendingFilterIntent: string | undefined
  let renderRevision = 0
  let announcedRenderRevision: number | undefined
  let debounceTimer: ReturnType<typeof setTimeout> | undefined
  let formTimer: ReturnType<typeof setTimeout> | undefined
  let loadingPresentationTimer: ReturnType<typeof setTimeout> | undefined
  let loadingPresentationVisible = false
  let remoteLoadingPresentationEnabled = false
  let openTimer: ReturnType<typeof setTimeout> | undefined
  let clearHidePromise: Promise<void> | undefined
  let clearing = false
  let tagTooltipOpenRevision = 0
  // Prevents a replaced tag anchor from immediately reopening its tooltip.
  let blockedTagTooltipPointer: readonly [x: number, y: number] | undefined
  let activeTooltipTag: HTMLElement | undefined
  let activeTagTooltip: WaTooltip | undefined
  let openIntentRevision = 0
  let recoveredOpenIntentRevision: number | undefined
  let rejectedShowPendingHide = false
  let hidingOpenIntentRevision = 0
  let hidingWasDismissal = false
  let dismissalRequested = false
  let emptyStateVisible = false
  let observer: IntersectionObserver | undefined
  let iconObserver: IntersectionObserver | undefined
  let iconScrollCleanup: Cleanup = () => undefined
  let anchorSlotCleanup: Cleanup = () => undefined
  let iconScrollFrame: number | undefined
  let iconScrollTarget: Element | undefined
  let lastTagLayoutFingerprint: string | undefined
  let tagFitRevision = 0
  let tagFitRequested = false
  let tagFitRunning = false
  let tagResizeObserver: ResizeObserver | undefined
  let nextOptionTokenId = 0
  const allocatedOptionTokens = new Set<string>()
  const retiredOptionValues = new Set<string>()
  let tokenByValue = new Map<string, string>()
  let valueByToken = new Map<string, string>()
  let boundComboboxInput: HTMLInputElement | undefined
  let nativeMouseFocusInput: HTMLInputElement | undefined
  let nativeMouseFocusTimer: ReturnType<typeof setTimeout> | undefined
  let cleanupComboboxInputSelection: Cleanup = () => undefined

  // Clears the short-lived focus intent created by one primary mouse gesture.
  const clearNativeMouseFocusIntent = (): void => {
    if (nativeMouseFocusTimer !== undefined) {
      clearTimeout(nativeMouseFocusTimer)
      nativeMouseFocusTimer = undefined
    }
    nativeMouseFocusInput = undefined
  }

  // Lets native mousedown focus without WA replacing its selection with select-all.
  const handleComboboxInputFocus: EventListener = (event): void => {
    const nativeMouseFocus =
      active &&
      connected &&
      nativeMouseFocusInput !== undefined &&
      comesFromComboboxInput(event, nativeMouseFocusInput)
    clearNativeMouseFocusIntent()
    if (nativeMouseFocus) {
      event.stopImmediatePropagation()
    }
  }

  // Collapses a blurred range so the next mouse gesture starts a new selection.
  const handleComboboxInputBlur: EventListener = (event): void => {
    clearNativeMouseFocusIntent()
    const input = event.composedPath()[0]
    if (
      !(
        active &&
        connected &&
        !server.multiple &&
        input instanceof window.HTMLInputElement
      )
    ) {
      return
    }

    const { selectionDirection, selectionEnd, selectionStart } = input
    if (
      selectionStart !== null &&
      selectionEnd !== null &&
      selectionStart !== selectionEnd
    ) {
      const caret =
        selectionDirection === 'backward' ? selectionStart : selectionEnd
      input.setSelectionRange(caret, caret)
    }
  }

  // Bypasses WA's cancelling wrapper while retaining native input defaults.
  const handleComboboxInputMouseEvent: EventListener = (event): void => {
    if (active && connected && !server.multiple && !server.disabled) {
      event.stopPropagation()
    }
  }

  // Moves the listener when WA replaces its rendered input node.
  const bindRenderedComboboxInput = (input: HTMLInputElement): void => {
    if (input === boundComboboxInput) {
      return
    }

    cleanupComboboxInputSelection()
    boundComboboxInput = input
    cleanupComboboxInputSelection = composeCleanup(
      listen(input, 'focus', handleComboboxInputFocus, true),
      listen(input, 'blur', handleComboboxInputBlur),
      listen(input, 'mousedown', handleComboboxInputMouseEvent),
      listen(input, 'click', handleComboboxInputMouseEvent)
    )
  }

  // Prepares the rendered single input before its native mouse events continue.
  const prepareNativeMouseSelection = (event: Event): boolean => {
    const input = currentComboboxInput()
    if (
      !(
        active &&
        connected &&
        !server.multiple &&
        input &&
        comesFromComboboxInput(event, input)
      )
    ) {
      return false
    }

    bindRenderedComboboxInput(input)
    if (isPrimaryPointerGesture(event) && !server.disabled) {
      clearNativeMouseFocusIntent()
      nativeMouseFocusInput = input
      nativeMouseFocusTimer = setTimeout(clearNativeMouseFocusIntent, 0)
    }
    return true
  }

  const cleanupInputSelection = (): void => {
    clearNativeMouseFocusIntent()
    cleanupComboboxInputSelection()
    boundComboboxInput = undefined
    cleanupComboboxInputSelection = () => undefined
  }

  // Starts one deferred icon request after its option approaches the viewport.
  const loadDeferredIcon = (icon: HTMLElement): void => {
    const src = icon.dataset.deferredSrc
    if (src) {
      icon.setAttribute('src', src)
      delete icon.dataset.deferredSrc
    }
  }

  // Resolves WA's scroll container after the custom element has rendered.
  const listboxElement = (): Element | undefined =>
    combobox.listbox instanceof window.Element ? combobox.listbox : undefined

  // Loads deferred icons whose option rows are in or near the listbox viewport.
  const loadApproachingDeferredIcons = (): void => {
    const listbox = listboxElement()
    if (!listbox) {
      return
    }

    const listboxBounds = listbox.getBoundingClientRect()
    if (listboxBounds.width === 0 || listboxBounds.height === 0) {
      return
    }

    const icons = combobox.querySelectorAll<HTMLElement>(
      'wa-icon[data-deferred-src]'
    )
    for (const icon of icons) {
      const option = icon.closest('wa-option')
      if (!option) {
        loadDeferredIcon(icon)
        continue
      }

      const optionBounds = option.getBoundingClientRect()
      if (
        optionBounds.bottom >=
          listboxBounds.top - OPTION_ICON_PRELOAD_MARGIN_PX &&
        optionBounds.top <= listboxBounds.bottom + OPTION_ICON_PRELOAD_MARGIN_PX
      ) {
        loadDeferredIcon(icon)
      }
    }
  }

  // Coalesces scroll-driven icon visibility checks to one per rendered frame.
  const scheduleApproachingDeferredIcons = (): void => {
    if (!(active && connected) || iconScrollFrame !== undefined) {
      return
    }
    iconScrollFrame = window.requestAnimationFrame(() => {
      iconScrollFrame = undefined
      if (active && connected) {
        loadApproachingDeferredIcons()
      }
    })
  }

  // Keeps nested listbox scrolling reliable when intersection delivery stalls.
  const setupDeferredIconScroll = (): void => {
    const listbox = listboxElement()
    if (!listbox || listbox === iconScrollTarget) {
      return
    }
    iconScrollCleanup()
    iconScrollTarget = listbox
    iconScrollCleanup = listen(
      listbox,
      'scroll',
      scheduleApproachingDeferredIcons,
      { passive: true }
    )
  }

  // Tracks icon rows after any option replacement or popup visibility change.
  const observeDeferredIcons = (): void => {
    setupDeferredIconScroll()
    if (combobox.open) {
      scheduleApproachingDeferredIcons()
    }
    const icons = Array.from(
      combobox.querySelectorAll<HTMLElement>('wa-icon[data-deferred-src]')
    )
    if (!iconObserver) {
      for (const icon of icons) {
        loadDeferredIcon(icon)
      }
      return
    }
    iconObserver.disconnect()
    for (const icon of icons) {
      iconObserver.observe(icon.closest('wa-option') ?? icon)
    }
  }

  // Allocates each opaque token once so a stale WA value cannot select new data.
  const allocateOptionToken = (): string => {
    let token = `option-${nextOptionTokenId++}`
    while (allocatedOptionTokens.has(token)) {
      token = `option-${nextOptionTokenId++}`
    }
    allocatedOptionTokens.add(token)
    return token
  }

  // Replaces canonical selection and invalidates tokens removed from the draft.
  const replaceSelectedValues = (values: readonly string[]): void => {
    const nextValues = server.multiple ? values : values.slice(0, 1)
    const nextValueSet = new Set(nextValues)
    for (const value of selectedValues) {
      if (!nextValueSet.has(value)) {
        retiredOptionValues.add(value)
      }
    }
    for (const value of nextValues) {
      retiredOptionValues.delete(value)
    }
    selectedValues = nextValues
  }

  // Returns the currently displayed option records.
  const displayedOptions = (): readonly ComboboxOption[] =>
    configuration.dataLoadEvent ? remoteOptions(remoteState) : staticOptions

  // Adds fallback labels only when a selected value lacks its bootstrap item.
  const optionsWithSelectedFallbacks = (
    options: readonly ComboboxOption[]
  ): readonly ComboboxOption[] => {
    const knownValues = new Set(options.map((option) => option.value))
    const fallbacks = selectedValues
      .filter((value) => !knownValues.has(value))
      .map((value) => ({ label: value, value }))
    return [...options, ...fallbacks]
  }

  // Adopts matching SSR options without replacing their stable light-DOM nodes.
  const adoptRenderedOptions = (): boolean => {
    const expected = optionsWithSelectedFallbacks(displayedOptions())
    const rendered = Array.from(combobox.querySelectorAll('wa-option'))

    if (rendered.length !== expected.length) {
      return false
    }

    const adopted = expected.flatMap((option, index) => {
      const element = rendered[index]
      const token = element?.value

      return element &&
        token &&
        !OPTION_TOKEN_SPACE_PATTERN.test(token) &&
        element.dataset.value === option.value &&
        element.label === option.label &&
        element.disabled === (option.disabled === true)
        ? [{ element, index, option, token }]
        : []
    })

    if (
      adopted.length !== expected.length ||
      new Set(adopted.map(({ token }) => token)).size !== adopted.length
    ) {
      return false
    }

    tokenByValue = new Map(
      adopted.map(({ option, token }) => [option.value, token])
    )
    valueByToken = new Map(
      adopted.map(({ option, token }) => [token, option.value])
    )
    for (const { token } of adopted) {
      allocatedOptionTokens.add(token)
    }

    // Releases SSR-only defaults after canonical values become controller-owned.
    for (const { element } of adopted) {
      element.defaultSelected = false
      element.removeAttribute('selected')
    }
    combobox.defaultValue = null

    if (server.decoration !== 'none') {
      for (const { element, index, option } of adopted) {
        element.replaceChildren(
          ...optionContent(
            option,
            server.decoration,
            index < EAGER_OPTION_ICON_COUNT ? 'eager' : 'visible'
          )
        )
      }
    }

    return true
  }

  // Maps the WA control's opaque value back to canonical form data.
  const valuesFromControl = (): readonly string[] => {
    const controlValue = combobox.value
    const tokens = combobox.multiple
      ? Array.isArray(controlValue)
        ? controlValue
        : []
      : isString(controlValue) && controlValue !== ''
        ? [controlValue]
        : combobox.valueInput.value === ''
          ? []
          : [combobox.valueInput.value]

    return tokens.flatMap((token) => {
      const value = valueByToken.get(token)
      return value ? [value] : []
    })
  }

  // Returns one displayed option by canonical value.
  const optionForValue = (value: string): ComboboxOption | undefined =>
    displayedOptions().find((option) => option.value === value)

  // Detects text edits that replace the label of a selected single value.
  const inputReplacesSingleSelection = (inputValue: string): boolean => {
    const selectedValue = selectedValues[0]
    const selectedOption = selectedValue
      ? optionForValue(selectedValue)
      : undefined

    return (
      !server.multiple &&
      selectedOption !== undefined &&
      inputValue !== selectedOption.label
    )
  }

  // Restores controller-owned slotted content after WA finishes filtering it.
  const restoreAuxiliaryContentAfterUpdate = (): void => {
    queueMicrotask(() => {
      Promise.resolve(combobox.updateComplete)
        .then(() => {
          if (active) {
            synchronizeStaticEmptyState()
            openStaticEmptyPopup()
          }
        })
        .catch((error) =>
          captureFailure(error, 'restore-auxiliary-content', root)
        )
    })
  }

  // Limits remote navigation to values returned by the active query.
  const navigableRemoteOptions = (
    options: readonly WaOption[]
  ): readonly WaOption[] => {
    if (!configuration.dataLoadEvent) {
      return options
    }

    const loadedValues = (() => {
      switch (remoteState.status) {
        case 'empty':
          return new Set<string>()
        case 'ready':
          return new Set(remoteState.loadedValues)
        case 'loading':
          if (remoteState.page > 0) {
            return new Set(remoteState.loadedValues)
          }
          return emptyStateVisible ? new Set<string>() : undefined
        case 'failure':
        case 'not-asked':
          return undefined
      }
    })()

    return loadedValues
      ? options.filter((option) => {
          if (isCreateOption(option)) {
            return creationReady()
          }
          const value = option.dataset.value
          return value !== undefined && loadedValues.has(value)
        })
      : options
  }

  // Rebinds the documented navigation state after WA's filtering pass.
  const synchronizeRemoteNavigation = (): void => {
    const options = Array.from(combobox.querySelectorAll('wa-option'))
    const navigableOptions = navigableRemoteOptions(options)
    const navigableSet = new Set(navigableOptions)

    for (const option of options) {
      option.hidden = !navigableSet.has(option)
    }
    synchronizeOptionNavigation(
      combobox,
      options,
      navigableOptions,
      combobox.currentOption?.dataset.value
    )
  }

  // Keeps retained selections out of empty-query navigation without hiding creation.
  const synchronizeEmptyNavigation = (): void => {
    if (!emptyStateVisible) {
      return
    }
    Reflect.set(combobox, 'hasInputSinceOpening', true)
    if (configuration.dataLoadEvent) {
      synchronizeRemoteNavigation()
      return
    }

    const options = Array.from(combobox.querySelectorAll('wa-option'))
    synchronizeOptionNavigation(combobox, options, [], undefined)
  }

  // Derives static empty feedback from WA's completed native filtering pass.
  const synchronizeStaticEmptyState = (): void => {
    if (configuration.dataLoadEvent) {
      return
    }

    const inputValue = readInputValue()
    if (wantsOpen && inputValue !== '') {
      Reflect.set(combobox, 'hasInputSinceOpening', true)
      refreshNativeFiltering()
    }

    const options = Array.from(combobox.querySelectorAll('wa-option'))
    const navigableOptions = options.filter(
      (option) => !(option.hidden || option.disabled)
    )
    emptyStateVisible = wantsOpen && !(creating || navigableOptions.length > 0)

    if (emptyStateVisible) {
      synchronizeEmptyNavigation()
    } else if (wantsOpen && inputValue !== '') {
      synchronizeOptionNavigation(
        combobox,
        options,
        navigableOptions,
        combobox.currentOption?.dataset.value
      )
    }
  }

  // Opens static empty feedback without WA's selectable-option precondition.
  const openStaticEmptyPopup = (): void => {
    if (
      !configuration.dataLoadEvent &&
      emptyStateVisible &&
      wantsOpen &&
      !clearHidePromise &&
      active &&
      connected &&
      !server.disabled &&
      !server.readonly &&
      !combobox.open
    ) {
      combobox.open = true
    }
  }

  // Mirrors one single selection into the controller-owned start slot.
  const renderSelectedPrefix = (
    value: string | undefined = selectedValues[0]
  ): void => {
    const option =
      !server.multiple && server.decoration !== 'none' && value
        ? optionForValue(value)
        : undefined
    const decoration = option
      ? leadingDecoration(option, server.decoration)
      : undefined

    prefix.replaceChildren(...(decoration ? [decoration] : []))
    prefix.toggleAttribute('data-visible', decoration !== undefined)
    prefix.hidden = decoration === undefined
  }

  // Restores canonical selection after WA processes a slot update.
  const restoreControlSelection = (
    inputRestoration: InputRestoration = 'none'
  ): void => {
    const revision = renderRevision
    Promise.resolve(combobox.updateComplete)
      .then(() => {
        if (!active || revision !== renderRevision) {
          return
        }

        const tokens = selectedValues.flatMap((value) => {
          const token = tokenByValue.get(value)
          return token ? [token] : []
        })
        const value = server.multiple ? tokens : (tokens[0] ?? null)
        const restoreDraft = (): void => {
          if (
            inputRestoration !== 'none' &&
            !server.multiple &&
            (inputRestoration === 'draft' || selectedValues.length === 0) &&
            readInputValue() !== inputDraft
          ) {
            combobox.inputValue = inputDraft
          }
        }

        if (isDeepEqual(combobox.value, value)) {
          restoreDraft()
          renderSelectedPrefix()
          requestTagLayoutFit()
          return
        }

        controlWriteRevision += 1
        const writeRevision = controlWriteRevision
        applyingControlState = true
        combobox.value = value
        renderSelectedPrefix()

        return Promise.resolve(combobox.updateComplete).then(() => {
          restoreDraft()
          requestTagLayoutFit()
          if (writeRevision === controlWriteRevision) {
            applyingControlState = false
          }
        })
      })
      .catch((error) => captureFailure(error, 'restore-selection', root))
  }

  // Replaces only the client-owned option nodes and rebuilds token maps.
  const renderOptions = (): void => {
    hideTagTooltip()
    const previousCurrentValue = combobox.currentOption?.dataset.value
    const previousTokenByValue = tokenByValue
    renderRevision += 1
    tokenByValue = new Map()
    valueByToken = new Map()

    const nodes = optionsWithSelectedFallbacks(displayedOptions()).map(
      (option, index) => {
        const token =
          (retiredOptionValues.has(option.value)
            ? undefined
            : previousTokenByValue.get(option.value)) ?? allocateOptionToken()
        retiredOptionValues.delete(option.value)
        tokenByValue.set(option.value, token)
        valueByToken.set(token, option.value)
        return optionElement(
          option,
          token,
          server.decoration,
          index < EAGER_OPTION_ICON_COUNT ? 'eager' : 'visible'
        )
      }
    )

    for (const stale of combobox.querySelectorAll('wa-option')) {
      stale.remove()
    }
    combobox.append(...nodes)
    observeDeferredIcons()
    const navigableOptions = navigableRemoteOptions(nodes)
    const navigableSet = new Set(navigableOptions)
    for (const node of nodes) {
      node.hidden = !navigableSet.has(node)
    }
    if (configuration.dataLoadEvent && combobox.open) {
      synchronizeOptionNavigation(
        combobox,
        nodes,
        navigableOptions,
        previousCurrentValue
      )
    }
    restoreControlSelection('empty-draft')
    restoreAuxiliaryContentAfterUpdate()
    observeLastLoadedOption()
  }

  // Customizes native WA multi-tags with safe DOM.
  const tagForOption: WaCombobox['getTag'] = (option) => {
    const value = option.dataset.value
    const canonicalOption = value ? optionForValue(value) : undefined
    const renderedOption = canonicalOption ?? {
      label: option.label,
      value: value ?? option.value
    }
    return tagElement(renderedOption, option.value, server.decoration)
  }

  // Installs one stable tag renderer whose closure reads current server state.
  const configureTags = (): void => {
    if (combobox.getTag !== tagForOption) {
      combobox.getTag = tagForOption
    }
  }

  // Reports whether rendered tags have displaced the reserved text-input space.
  const tagLayoutOverflows = (): boolean | undefined => {
    const tags =
      combobox.shadowRoot?.querySelector<HTMLElement>('[part~="tags"]')
    if (!tags || tags.clientWidth === 0) {
      return undefined
    }

    return (
      tags.scrollWidth - tags.clientWidth > TAG_LAYOUT_OVERFLOW_TOLERANCE_PX
    )
  }

  // Describes every input that can change the available or required tag width.
  const tagLayoutFingerprint = (): string | undefined => {
    const tags =
      combobox.shadowRoot?.querySelector<HTMLElement>('[part~="tags"]')
    if (!tags || tags.clientWidth === 0) {
      return undefined
    }

    return JSON.stringify({
      availableWidth: tags.clientWidth,
      decoration: server.decoration,
      options: selectedValues.map((value) => optionForValue(value) ?? value),
      size: combobox.size
    })
  }

  // Checks whether an asynchronous fit still owns the connected controller.
  const tagFitIsCurrent = (revision: number): boolean =>
    active && connected && revision === tagFitRevision

  // Applies one documented WA tag limit and waits for its resulting geometry.
  const renderTagLimit = async (
    limit: number,
    revision: number
  ): Promise<boolean> => {
    if (!tagFitIsCurrent(revision)) {
      return false
    }

    combobox.toggleAttribute('data-adaptive-tag-limit-minimum', limit === 1)
    if (combobox.maxOptionsVisible !== limit) {
      hideTagTooltip()
      combobox.maxOptionsVisible = limit
      await combobox.updateComplete
    }

    return tagFitIsCurrent(revision)
  }

  // Searches the monotonic tag-fit range with a bounded number of WA renders.
  const searchVisibleTagLimit = async (
    range: TagFitRange,
    revision: number
  ): Promise<number | undefined> => {
    if (!tagFitIsCurrent(revision)) {
      return undefined
    }
    if (range.lower > range.upper) {
      return range.best
    }

    const candidate = Math.floor((range.lower + range.upper) / 2)
    if (!(await renderTagLimit(candidate, revision))) {
      return undefined
    }
    const overflowing = tagLayoutOverflows()
    if (overflowing === undefined) {
      return undefined
    }

    return overflowing
      ? searchVisibleTagLimit({ ...range, upper: candidate - 1 }, revision)
      : searchVisibleTagLimit(
          { best: candidate, lower: candidate + 1, upper: range.upper },
          revision
        )
  }

  // Fits the greatest possible tag count while preserving the native input.
  const fitVisibleTags = async (revision: number): Promise<void> => {
    const selectedCount = combobox.selectedOptions.length
    if (!tagFitIsCurrent(revision)) {
      return
    }
    if (!(server.multiple && selectedCount > 0)) {
      lastTagLayoutFingerprint = undefined
      combobox.removeAttribute('data-adaptive-tag-limit-minimum')
      return
    }
    const fingerprint = tagLayoutFingerprint()
    const overflowing = tagLayoutOverflows()
    if (fingerprint === undefined || overflowing === undefined) {
      return
    }
    if (fingerprint === lastTagLayoutFingerprint && !overflowing) {
      synchronizeOverflowTagAccessibility()
      return
    }

    const currentLimit = Math.min(
      Math.max(combobox.maxOptionsVisible, 1),
      selectedCount
    )
    if (!(await renderTagLimit(currentLimit, revision))) {
      return
    }
    const currentOverflows = tagLayoutOverflows()
    if (currentOverflows === undefined) {
      return
    }
    const range: TagFitRange = currentOverflows
      ? { best: 1, lower: 1, upper: currentLimit - 1 }
      : {
          best: currentLimit,
          lower: currentLimit + 1,
          upper: selectedCount
        }
    const limit = await searchVisibleTagLimit(range, revision)
    if (
      limit === undefined ||
      !(await renderTagLimit(limit, revision)) ||
      !tagFitIsCurrent(revision)
    ) {
      return
    }
    synchronizeOverflowTagAccessibility()
    lastTagLayoutFingerprint = tagLayoutFingerprint()
  }

  // Coalesces selection and resize changes into one serialized fitting pass.
  const runTagLayoutFit = async (): Promise<void> => {
    if (tagFitRunning) {
      return
    }

    tagFitRunning = true
    try {
      tagFitRequested = false
      const revision = tagFitRevision
      await combobox.updateComplete
      if (tagFitIsCurrent(revision)) {
        await fitVisibleTags(revision)
      }
    } catch (error) {
      captureFailure(error, 'tag-layout-fit', root)
    } finally {
      tagFitRunning = false
      if (active && connected && tagFitRequested) {
        queueMicrotask(runTagLayoutFit)
      }
    }
  }

  // Requests a fit without allowing overlapping Web Awesome update cycles.
  const requestTagLayoutFit = (): void => {
    if (!(active && connected && server.multiple)) {
      return
    }

    tagFitRevision += 1
    tagFitRequested = true
    if (!tagFitRunning) {
      queueMicrotask(runTagLayoutFit)
    }
  }

  // Finds the current WA tag through a composed pointer-event path.
  const eventTag = (event: Event): HTMLElement | undefined =>
    event
      .composedPath()
      .find(
        (target): target is HTMLElement =>
          target instanceof window.HTMLElement &&
          target.matches('wa-tag[part~="tag"]')
      )

  // Derives tooltip content only for clipped labels and hidden-value summaries.
  const tagTooltipPresentation = (
    tag: HTMLElement
  ): TagTooltipPresentation | undefined => {
    if (tag.dataset.value === undefined) {
      const hiddenOptions =
        combobox.maxOptionsVisible > 0
          ? combobox.selectedOptions.slice(combobox.maxOptionsVisible)
          : []
      const hiddenLabels = hiddenOptions.map((option) => {
        const value = option.dataset.value
        return (
          (value ? optionForValue(value) : undefined)?.label ?? option.label
        )
      })
      const content = hiddenLabels.join(', ')
      if (content === '') {
        return undefined
      }
      return {
        content,
        kind: 'overflow',
        label: `+${hiddenOptions.length}`
      }
    }

    const label = tag.querySelector<HTMLElement>('[part="combobox-tag-label"]')
    const content =
      tag.shadowRoot?.querySelector<HTMLElement>('[part="content"]')
    const overflowed = Boolean(
      (label && label.scrollWidth > label.clientWidth) ||
        (content && content.scrollWidth > content.clientWidth)
    )
    return overflowed
      ? { content: label?.textContent ?? '', kind: 'label' }
      : undefined
  }

  // Exposes hidden summary labels without making the compact tag interactive.
  const synchronizeOverflowTagAccessibility = (): void => {
    const tag = combobox.shadowRoot?.querySelector<HTMLElement>(
      'wa-tag[part~="tag"]:not([data-value])'
    )
    const presentation = tag ? tagTooltipPresentation(tag) : undefined
    if (!(tag && presentation?.kind === 'overflow')) {
      return
    }

    tag.setAttribute('role', 'group')
    tag.setAttribute(
      'aria-label',
      `${presentation.label}: ${presentation.content}`
    )
  }

  // Waits for WA to render and fit the current tag set before naming it.
  const synchronizeOverflowTagAccessibilityAfterUpdate = (): void => {
    Promise.resolve(combobox.updateComplete)
      .then(() => {
        if (active) {
          requestTagLayoutFit()
        }
      })
      .catch((error) =>
        captureFailure(error, 'tag-summary-accessibility', root)
      )
  }

  // Closes and removes the tooltip owned by the previously hovered tag.
  const hideTagTooltip = (): void => {
    const tooltip = activeTagTooltip
    activeTooltipTag = undefined
    activeTagTooltip = undefined
    tagTooltipOpenRevision += 1
    if (tooltip) {
      tooltip.hide().catch(() => undefined)
      tooltip.remove()
    }
  }

  // Opens one same-root tooltip after validating the current tag presentation.
  const showTagTooltip = (tag: HTMLElement): void => {
    const presentation = tagTooltipPresentation(tag)
    if (!(server.multiple && presentation)) {
      hideTagTooltip()
      return
    }

    hideTagTooltip()
    const token = tag.dataset.value
    const canonicalValue = token ? valueByToken.get(token) : undefined
    const encodedValue =
      canonicalValue === undefined ? undefined : domIdToken(canonicalValue)
    const anchorToken =
      encodedValue === undefined ? 'overflow' : encodedValue || 'empty'
    tag.id = `${combobox.id}-tag-tooltip-target-${anchorToken}`
    const tooltip = tagTooltipElement(tag.id, combobox.id)
    tooltip.textContent = presentation.content
    tooltip.dataset.kind = presentation.kind
    const tooltipRoot = tag.getRootNode()
    if (!(tooltipRoot instanceof window.ShadowRoot)) {
      return
    }
    tooltipRoot.append(tooltip)
    activeTooltipTag = tag
    activeTagTooltip = tooltip
    const revision = ++tagTooltipOpenRevision
    Promise.resolve(tooltip.updateComplete)
      .then(() => {
        if (
          active &&
          connected &&
          activeTooltipTag === tag &&
          activeTagTooltip === tooltip &&
          revision === tagTooltipOpenRevision
        ) {
          return tooltip.show()
        }
        return undefined
      })
      .catch((error) => {
        if (active && connected) {
          captureFailure(error, 'tag-tooltip-open-state', root)
        }
      })
  }

  // Tracks the current tag through the host's composed pointer path.
  const handleTagPointerMove: EventListener = (event): void => {
    if (blockedTagTooltipPointer) {
      const [x, y] = blockedTagTooltipPointer
      if (
        !(event instanceof window.PointerEvent) ||
        (Math.abs(event.clientX - x) <= 1 && Math.abs(event.clientY - y) <= 1)
      ) {
        hideTagTooltip()
        return
      }
      blockedTagTooltipPointer = undefined
    }

    const tag = eventTag(event)
    if (tag && tag !== activeTooltipTag) {
      showTagTooltip(tag)
    } else if (!tag && activeTooltipTag) {
      hideTagTooltip()
    }
  }

  // Rearms tag tooltips when the pointer leaves the activated control.
  const handleTagPointerLeave = (): void => {
    blockedTagTooltipPointer = undefined
    hideTagTooltip()
  }

  // Announces asynchronous status without changing the control's geometry.
  const announceStatus = (
    message: string,
    kind: StatusKind = 'normal'
  ): void => {
    const announcerRole = kind === 'failure' ? 'alert' : 'status'
    const announcerLive = kind === 'failure' ? 'assertive' : 'polite'
    if (
      announcer.textContent !== message ||
      announcer.getAttribute('role') !== announcerRole ||
      announcer.getAttribute('aria-live') !== announcerLive
    ) {
      announcer.setAttribute('role', announcerRole)
      announcer.setAttribute('aria-live', announcerLive)
      announcer.textContent = message
    }
  }

  // Keeps helper and visible status feedback in one hint slot.
  const setVisibleStatus = (
    message: string,
    kind: StatusKind = 'normal'
  ): void => {
    helper.textContent = server.helperText
    status.textContent = message
    status.dataset.status = kind
    const visible = server.helperText !== '' || message !== ''
    hint.hidden = !visible
    const slot = visible ? 'hint' : INACTIVE_HINT_SLOT
    if (hint.slot !== slot) {
      hint.slot = slot
    }
  }

  // Presents visible feedback and its matching live-region announcement.
  const setStatus = (message: string, kind: StatusKind = 'normal'): void => {
    setVisibleStatus(message, kind)
    announceStatus(message, kind)
  }

  // Describes the server-owned validation state without performing DOM effects.
  const validationStatus = (): readonly [string, StatusKind] =>
    server.invalid
      ? [server.errorMessage || 'Invalid value', 'failure']
      : ['', 'normal']

  // Keeps validation visible while loading is announced independently.
  const renderVisibleValidationStatus = (): void => {
    const [message, kind] = validationStatus()
    setVisibleStatus(message, kind)
  }

  // Keeps validation visible while a short request remains visually silent.
  const renderValidationStatus = (): void => {
    const [message, kind] = validationStatus()
    setStatus(message, kind)
  }

  // Prioritizes assertive validation feedback over a polite loading update.
  const announceLoadingStatus = (): void => {
    if (server.invalid) {
      const [message, kind] = validationStatus()
      announceStatus(message, kind)
    } else {
      announceStatus(configuration.loadingMessage)
    }
  }

  // Reveals loading UI only when work survives the anti-flicker threshold.
  const updateLoadingPresentation = (loading: boolean): boolean => {
    const wasVisible = loadingPresentationVisible
    if (!loading) {
      if (loadingPresentationTimer !== undefined) {
        clearTimeout(loadingPresentationTimer)
        loadingPresentationTimer = undefined
      }
      loadingPresentationVisible = false
    } else if (
      !loadingPresentationVisible &&
      loadingPresentationTimer === undefined
    ) {
      loadingPresentationTimer = setTimeout(() => {
        loadingPresentationTimer = undefined
        if (
          active &&
          connected &&
          (remoteState.status === 'loading' || creating)
        ) {
          loadingPresentationVisible = true
          spinner.setAttribute('data-loading', '')
          spinner.hidden = false
          announceLoadingStatus()
          requestTagLayoutFit()
        }
      }, LOADING_PRESENTATION_DELAY_MS)
    }

    spinner.toggleAttribute('data-loading', loadingPresentationVisible)
    spinner.hidden = !loadingPresentationVisible
    if (wasVisible !== loadingPresentationVisible) {
      requestTagLayoutFit()
    }
    return loadingPresentationVisible
  }

  // Enables creation only after the current remote query has settled.
  const creationReady = (): boolean => {
    const inputValue = readInputValue()
    if (
      !(configuration.createEvent && connected) ||
      server.disabled ||
      server.readonly ||
      inputValue.trim() === ''
    ) {
      return false
    }

    if (!configuration.dataLoadEvent) {
      return true
    }

    switch (remoteState.status) {
      case 'empty':
      case 'ready':
        return remoteState.filter === inputValue
      case 'failure':
      case 'loading':
      case 'not-asked':
        return false
    }
  }

  // Refreshes WA's native create option after an external remote transition.
  const refreshNativeFiltering = (): void => {
    const updateFiltering: unknown = Reflect.get(
      combobox,
      'updateFilteredOptions'
    )
    if (typeof updateFiltering === 'function') {
      Reflect.apply(updateFiltering, combobox, [])
    }
  }

  // Applies loading, empty, and failure presentation from the state machine.
  const renderRemoteStatus = (): void => {
    const busy = remoteState.status === 'loading' || creating
    const presentLoading =
      creating ||
      (remoteState.status === 'loading' && remoteLoadingPresentationEnabled)
    const loadingPresented = updateLoadingPresentation(presentLoading)
    combobox.toggleAttribute('aria-busy', busy)
    const allowCreate =
      Boolean(configuration.createEvent) &&
      !busy &&
      connected &&
      !server.disabled &&
      !server.readonly &&
      (!configuration.dataLoadEvent || creationReady())
    if (combobox.allowCreate !== allowCreate) {
      combobox.allowCreate = allowCreate
      refreshNativeFiltering()
    }

    if (configuration.dataLoadEvent) {
      if (remoteState.status === 'empty') {
        emptyStateVisible = !creationReady()
      } else if (remoteState.status !== 'loading') {
        emptyStateVisible = false
      }
    } else {
      synchronizeStaticEmptyState()
    }

    if (combobox.open) {
      synchronizeEmptyNavigation()
    }

    if (creationFailure) {
      setStatus(creationFailure, 'failure')
      return
    }

    switch (remoteState.status) {
      case 'loading':
        if (loadingPresented) {
          renderVisibleValidationStatus()
          announceLoadingStatus()
        } else {
          renderValidationStatus()
        }
        break
      case 'empty':
        renderValidationStatus()
        break
      case 'failure':
        setStatus(remoteState.message, 'failure')
        if (!remoteState.options.some((option) => !option.disabled)) {
          combobox.hide().catch(() => undefined)
        }
        break
      case 'not-asked':
      case 'ready':
        renderValidationStatus()
        break
    }
  }

  // Allows initial remote geometry only after page zero has settled.
  const remotePopupReady = (): boolean => {
    if (!configuration.dataLoadEvent) {
      return true
    }
    if (pageZeroSearchIsPending()) {
      return false
    }

    const hasSelectableOptions = remoteState.options.some(
      (option) => !option.disabled
    )

    switch (remoteState.status) {
      case 'not-asked':
        return false
      case 'empty':
        return true
      case 'loading':
        return remoteState.page > 0 && hasSelectableOptions
      case 'ready':
        return hasSelectableOptions || creationReady()
      case 'failure':
        return hasSelectableOptions
    }
  }

  // Identifies a typed query whose first remote page has not settled yet.
  const pageZeroSearchIsPending = (): boolean => {
    const pendingInput = pendingFilterIntent ?? readInputValue()
    const pendingFilterStartsQuery =
      pendingFilterIntent !== undefined &&
      (remoteState.status === 'not-asked' ||
        pendingFilterIntent !== remoteState.filter)
    return (
      configuration.dataLoadEvent !== undefined &&
      pendingInput !== '' &&
      ((remoteState.status === 'loading' && remoteState.page === 0) ||
        pendingFilterStartsQuery)
    )
  }

  // Restores remote navigation before opening through WA or the empty adapter.
  const openPopup = (): void => {
    if (configuration.dataLoadEvent) {
      synchronizeRemoteNavigation()
    }
    if (emptyStateVisible && !hasVisibleSelectableOption(combobox)) {
      synchronizeEmptyNavigation()
      combobox.open = true
      return
    }
    combobox.show().catch(() => undefined)
  }

  // Opens a settled popup after WA finishes the pointer gesture that requested it.
  const schedulePopupOpen = (delayMs = 0): void => {
    if (openTimer !== undefined) {
      clearTimeout(openTimer)
    }
    const intentRevision = openIntentRevision
    const revision = renderRevision
    openTimer = setTimeout(() => {
      openTimer = undefined
      const openIfCurrent = (): void => {
        if (
          active &&
          connected &&
          intentRevision === openIntentRevision &&
          revision === renderRevision &&
          wantsOpen &&
          !dismissalRequested &&
          remotePopupReady() &&
          !server.disabled &&
          !server.readonly
        ) {
          if (!configuration.dataLoadEvent) {
            renderRemoteStatus()
          }
          synchronizeStaticEmptyState()
          if (!combobox.open) {
            openPopup()
          }
        }
      }
      const pendingHide = clearHidePromise
      if (pendingHide) {
        pendingHide.then(openIfCurrent)
      } else {
        openIfCurrent()
      }
    }, delayMs)
  }

  // Applies the version-pinned readonly seam and fails closed if it disappears.
  const applyReadonly = (): void => {
    combobox.toggleAttribute('readonly', server.readonly)
    combobox.setAttribute('aria-readonly', String(server.readonly))

    Promise.resolve(combobox.updateComplete)
      .then(() => {
        if (!active) {
          return
        }

        const input = currentComboboxInput()
        if (input) {
          input.readOnly = server.readonly
          combobox.disabled = server.disabled || !connected
        } else {
          combobox.disabled = server.disabled || server.readonly || !connected
        }

        if (server.readonly || server.disabled || !connected) {
          combobox.hide().catch(() => undefined)
        }
      })
      .catch((error) => {
        combobox.disabled = true
        captureFailure(error, 'readonly-adapter', root)
      })
  }

  // Let option text size the menu while Web Awesome keeps it within the viewport.
  const applyPopupLayout = (): void => {
    Promise.resolve(combobox.updateComplete)
      .then(() => {
        if (!active) {
          return
        }

        const popup = combobox.popup
        if (
          popup.distance !== POPUP_DISTANCE_PX ||
          popup.hasAttribute('sync') ||
          popup.autoSize !== 'both' ||
          popup.autoSizePadding !== POPUP_VIEWPORT_PADDING_PX ||
          popup.shiftPadding !== POPUP_VIEWPORT_PADDING_PX
        ) {
          popup.distance = POPUP_DISTANCE_PX
          popup.removeAttribute('sync')
          popup.autoSize = 'both'
          popup.autoSizePadding = POPUP_VIEWPORT_PADDING_PX
          popup.shiftPadding = POPUP_VIEWPORT_PADDING_PX
          popup.reposition()
        }
      })
      .catch((error) => captureFailure(error, 'popup-layout', root))
  }

  // Applies server validation without relying on undocumented invalid state.
  const applyValidity = (): void => {
    combobox.setAttribute('aria-invalid', String(server.invalid))
    combobox.setCustomValidity(
      server.invalid ? server.errorMessage || 'Invalid value' : ''
    )
  }

  // Synchronizes all visible server-owned control properties.
  const applyServerPresentation = (): void => {
    const size = isComboboxSize(server.size) ? server.size : 'm'
    const withClear = server.clearable && !server.readonly
    const tagLayoutChanged =
      combobox.size !== size ||
      combobox.multiple !== server.multiple ||
      combobox.withClear !== withClear

    if (combobox.label !== server.label) {
      combobox.label = server.label
    }
    if (combobox.placeholder !== server.placeholder) {
      combobox.placeholder = server.placeholder
    }
    if (combobox.size !== size) {
      combobox.size = size
    }
    if (combobox.multiple !== server.multiple) {
      combobox.multiple = server.multiple
    }
    if (combobox.withClear !== withClear) {
      combobox.withClear = withClear
    }
    if (combobox.required !== server.required) {
      combobox.required = server.required
    }
    carrier.disabled = server.disabled
    configureTags()
    if (tagLayoutChanged) {
      requestTagLayoutFit()
    }
    applyPopupLayout()
    applyReadonly()
    applyValidity()
    renderRemoteStatus()

    if (server.autofocus && !autofocusApplied) {
      autofocusApplied = true
      queueMicrotask(() => {
        if (active && connected && !server.disabled) {
          combobox.focus()
        }
      })
    }
  }

  // Writes canonical values before dispatching any form notification.
  const writeCanonicalValues = (
    values: readonly string[],
    notify: boolean
  ): void => {
    const normalized = server.multiple ? values : values.slice(0, 1)
    if (isDeepEqual(normalized, selectedValues)) {
      return
    }

    replaceSelectedValues(normalized)
    writeCarrier(carrier, selectedValues, server.disabled, configuration.target)
    renderSelectedPrefix()
    synchronizeOverflowTagAccessibilityAfterUpdate()

    if (!(notify && root.isConnected)) {
      return
    }

    if (formTimer !== undefined) {
      clearTimeout(formTimer)
    }

    const emit = (): void => {
      triggerForm(carrier.id, 'change', 'Combobox form trigger failed', root)
      formTimer = undefined
    }

    const delay = formUpdateDelay(configuration.debounceMs, server.multiple)

    if (delay > 0) {
      formTimer = setTimeout(emit, delay)
    } else {
      emit()
    }
  }

  // Applies a server value without echoing a new form edit.
  const restoreCanonicalValues = (
    values: readonly string[],
    replaceInputDraft: boolean
  ): void => {
    replaceSelectedValues(values)
    if (replaceInputDraft || selectedValues.length > 0) {
      singleDraftClearedSelection = false
    }
    if (replaceInputDraft && !server.multiple) {
      const selectedValue = selectedValues[0]
      inputDraft = selectedValue
        ? (optionForValue(selectedValue)?.label ?? selectedValue)
        : ''
    }
    writeCarrier(carrier, selectedValues, server.disabled, configuration.target)
    restoreControlSelection(replaceInputDraft ? 'draft' : 'none')
  }

  // Restores only a canonical selection that differs from the current draft.
  const restoreChangedCanonicalValues = (
    values: readonly string[],
    replaceInputDraft: boolean
  ): void => {
    const normalized = server.multiple ? values : values.slice(0, 1)
    if (replaceInputDraft || !isDeepEqual(normalized, selectedValues)) {
      restoreCanonicalValues(normalized, replaceInputDraft)
    }
  }

  // Observes the final loaded option to trigger automatic pagination.
  const observeLastLoadedOption = (): void => {
    observer?.disconnect()

    if (
      remoteState.status !== 'ready' ||
      remoteState.loadedValues.length >= remoteState.total ||
      !observer
    ) {
      return
    }

    const lastValue =
      remoteState.loadedValues[remoteState.loadedValues.length - 1]
    const option = Array.from(combobox.querySelectorAll('wa-option')).find(
      (candidate) => candidate.dataset.value === lastValue
    )
    if (option) {
      observer.observe(option)
    }
  }

  // Announces later remote renders without repeating WA's opening announcement.
  const announceRemoteResults = (revision: number): void => {
    if (
      combobox.open &&
      wantsOpen &&
      !dismissalRequested &&
      announcedRenderRevision !== undefined &&
      announcedRenderRevision !== revision
    ) {
      announceFilterResults(combobox)
      announcedRenderRevision = revision
    }
  }

  // Renders a completed remote transition and optionally reopens the listbox.
  const renderRemoteTransition = (): void => {
    const input = currentComboboxInput()
    const focusedInput =
      input && !server.multiple && combobox.matches(':focus-within')
        ? {
            revision: inputRevision,
            value: input.value,
            selectionStart: input.selectionStart,
            selectionEnd: input.selectionEnd
          }
        : undefined
    // Restores the caret only while the same input gesture still owns the draft.
    const restoreFocusedSelection = (): void => {
      const restoredInput = currentComboboxInput()
      if (
        focusedInput &&
        focusedInput.revision === inputRevision &&
        focusedInput.selectionStart !== null &&
        focusedInput.selectionEnd !== null &&
        restoredInput
      ) {
        const lastPosition = restoredInput.value.length
        restoredInput.setSelectionRange(
          Math.min(focusedInput.selectionStart, lastPosition),
          Math.min(focusedInput.selectionEnd, lastPosition)
        )
      }
    }

    renderOptions()
    renderRemoteStatus()
    const revision = renderRevision
    Promise.resolve(combobox.updateComplete)
      .then(() => {
        if (!active || revision !== renderRevision) {
          return
        }
        synchronizeRemoteNavigation()
        if (
          focusedInput &&
          focusedInput.revision === inputRevision &&
          readInputValue() !== focusedInput.value
        ) {
          combobox.inputValue = focusedInput.value
        }

        return Promise.resolve(combobox.updateComplete).then(() => {
          if (!(active && connected) || revision !== renderRevision) {
            return
          }
          restoreFocusedSelection()
          announceRemoteResults(revision)

          if (wantsOpen && !inputPopupOpenPending && remotePopupReady()) {
            const openDelayMs = remoteOptionsAreCurrent(remoteState, '')
              ? INITIAL_REMOTE_POPUP_DELAY_MS
              : 0
            schedulePopupOpen(openDelayMs)
          }
        })
      })
      .catch((error) => captureFailure(error, 'render-remote-options', root))
  }

  // Rejects replies superseded by either a started query or a queued keystroke.
  const requestCanSettle = (request: OptionPageRequest): boolean =>
    active &&
    connected &&
    requestIsCurrent(remoteState, request) &&
    (pendingFilterIntent === undefined ||
      pendingFilterIntent === request.filter)

  // Executes one already-versioned server page request.
  const executeRemoteRequest = (request: OptionPageRequest): void => {
    const event = configuration.dataLoadEvent
    if (!(event && connected && active)) {
      return
    }

    const payload = buildOptionPagePayload(request, server.loadParams)
    pushEventWithCallback(
      optionsHook,
      event,
      { ...payload },
      configuration.clientOptions ? undefined : configuration.target,
      DEFAULT_PUSH_EVENT_TIMEOUT_MS,
      connectionAbortController.signal
    )
      .then((reply) => {
        if (!requestCanSettle(request)) {
          return
        }

        const page = parseOptionPageReply(reply)
        if (!page) {
          throw new TypeError('Invalid combobox option response')
        }

        remoteState = receiveRemotePage(
          remoteState,
          request,
          page,
          selectedValues,
          configuration.failureMessage
        )
        renderRemoteTransition()
      })
      .catch((error) => {
        if (!requestCanSettle(request) || isAbortError(error)) {
          return
        }
        remoteState = failRemoteLoad(
          remoteState,
          request,
          configuration.failureMessage
        )
        renderRemoteStatus()
        captureFailure(error, 'data-load', root, {
          event,
          filter: request.filter,
          page: request.page
        })
      })
  }

  // Starts a new filter generation and debounces only its first page.
  const requestFilter = (filter: string, force = false): void => {
    if (!(configuration.dataLoadEvent && connected && active)) {
      return
    }

    if (
      !force &&
      (remoteState.status === 'loading' || remoteState.status === 'ready') &&
      remoteState.filter === filter
    ) {
      return
    }

    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer)
      debounceTimer = undefined
    }
    if (openTimer !== undefined) {
      clearTimeout(openTimer)
      openTimer = undefined
    }

    const transition = startRemoteQuery(remoteState, filter)
    remoteState = transition[0]
    const request = transition[1]
    observer?.disconnect()
    renderRemoteStatus()

    if (configuration.debounceMs && configuration.debounceMs > 0) {
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined
        executeRemoteRequest(request)
      }, configuration.debounceMs)
    } else {
      executeRemoteRequest(request)
    }
  }

  // Requests the next page when the observer reaches the loaded boundary.
  const requestNextPage = (): void => {
    const transition = startNextRemotePage(remoteState)
    if (!transition) {
      return
    }

    remoteState = transition[0]
    renderRemoteStatus()
    observer?.disconnect()
    executeRemoteRequest(transition[1])
  }

  // Applies one interactive open patch through the empty-popup adapter.
  const applyOpenState = (open: JsonValue | undefined): void => {
    if (!isBoolean(open) || open === combobox.open) {
      return
    }

    if (!open) {
      combobox.hide().catch(() => undefined)
      return
    }

    if (!configuration.dataLoadEvent) {
      wantsOpen = true
      synchronizeStaticEmptyState()
    }
    openPopup()
  }

  // Parses and applies one complete set of reconciled live props.
  const applyLiveValues = (values: LivePropValues): void => {
    const nextOptions = parseComboboxOptions(values.options)
    const canonicalPatch = canonicalValuesForPatch(root, values.values)
    const nextParams = isJsonObject(values.loadParams)
      ? values.loadParams
      : undefined
    const nextDecoration = isDecoration(values.decoration)
      ? values.decoration
      : undefined
    const optionsChanged =
      nextOptions !== undefined && !isDeepEqual(nextOptions, server.options)
    const paramsChanged =
      nextParams !== undefined && !isDeepEqual(nextParams, server.loadParams)
    const decorationChanged =
      nextDecoration !== undefined && nextDecoration !== server.decoration
    const canonicalValuesChanged =
      canonicalPatch.values !== undefined &&
      !isDeepEqual(canonicalPatch.values, selectedValues)

    if (canonicalValuesChanged) {
      hideTagTooltip()
    }

    server = mergeServerState(
      server,
      values,
      nextOptions,
      nextParams,
      nextDecoration
    )

    // Picks echoed back as server options must not reload and reorder an open list.
    const remoteOptionsChanged =
      optionsChanged && !remoteOptionsContain(remoteState, server.options)

    if (optionsChanged) {
      staticOptions = server.options
    }
    if (remoteOptionsChanged) {
      remoteState = mergeRemoteBootstrapOptions(remoteState, server.options)
    }
    if (paramsChanged) {
      remoteState = replaceRemoteBootstrapOptions(
        remoteState,
        remoteOptions(remoteState)
      )
    }
    if (canonicalPatch.values) {
      restoreChangedCanonicalValues(
        canonicalPatch.values,
        canonicalPatch.replaceInputDraft
      )
    }
    const displayedOptionsChanged = configuration.dataLoadEvent
      ? remoteOptionsChanged
      : optionsChanged

    // Re-rendering replaces every option and resets the list's scroll position.
    if (displayedOptionsChanged || paramsChanged || decorationChanged) {
      renderOptions()
    }

    applyServerPresentation()

    applyOpenState(values.open)

    if (
      (remoteOptionsChanged || paramsChanged) &&
      configuration.dataLoadEvent &&
      combobox.open
    ) {
      requestFilter(inputDraft, true)
    }
  }

  // Reads only the managed properties requested by the live-props engine.
  const readLiveValues = (propertyNames: readonly string[]): LivePropValues => {
    const allValues: LivePropValues = {
      options: server.options.map(optionLiveValue),
      loadParams: server.loadParams,
      label: server.label,
      placeholder: server.placeholder,
      size: server.size,
      multiple: server.multiple,
      clearable: server.clearable,
      disabled: server.disabled,
      readonly: server.readonly,
      required: server.required,
      invalid: server.invalid,
      errorMessage: server.errorMessage,
      helperText: server.helperText,
      decoration: server.decoration,
      autofocus: server.autofocus,
      open: combobox.open,
      values: selectedValues
    }

    return Object.fromEntries(
      propertyNames.flatMap((property) => {
        const value = allValues[property]
        return value === undefined ? [] : [[property, value] as const]
      })
    )
  }

  // Synchronizes native WA changes into the canonical carrier.
  const handleChange: EventListener = (event): void => {
    event.stopPropagation()
    pendingFilterIntent = undefined
    if (applyingControlState) {
      return
    }
    inputRevision += 1
    if (!(active && connected) || server.disabled || server.readonly) {
      restoreControlSelection()
      return
    }

    if (!server.multiple) {
      wantsOpen = false
      if (openTimer !== undefined) {
        clearTimeout(openTimer)
        openTimer = undefined
      }
    }
    writeCanonicalValues(valuesFromControl(), true)
    singleDraftClearedSelection = false
    hideTagTooltip()
  }

  // Records remote filter intent before shadow-DOM event propagation yields.
  const handleInputIntent: EventListener = (): void => {
    if (
      configuration.dataLoadEvent &&
      !clearing &&
      !server.disabled &&
      !server.readonly
    ) {
      pendingFilterIntent = currentComboboxInput()?.value ?? readInputValue()
      dismissalRequested = false
      remoteLoadingPresentationEnabled = true
      wantsOpen = true
    }
  }

  // Converts text input into a versioned remote filter request.
  const handleInput: EventListener = (event): void => {
    event.stopPropagation()
    if (clearing) {
      return
    }
    dismissalRequested = false
    restoreAuxiliaryContentAfterUpdate()
    if (server.disabled || server.readonly) {
      return
    }

    if (
      !server.multiple &&
      singleDraftClearedSelection &&
      selectedValues.length === 0 &&
      valuesFromControl().length > 0
    ) {
      const revision = ++inputRevision
      pendingFilterIntent = undefined
      // A real WA selection follows with change; only reject an orphaned input.
      queueMicrotask(() => {
        if (
          active &&
          connected &&
          revision === inputRevision &&
          singleDraftClearedSelection
        ) {
          restoreControlSelection('draft')
        }
      })
      return
    }

    const intendedInput = pendingFilterIntent ?? readInputValue()
    inputDraft = intendedInput
    remoteLoadingPresentationEnabled = true
    // A following WA change cancels selection input; typed drafts survive token reconciliation.
    const revision = ++inputRevision
    const controlMatchesCanonicalSelection = isDeepEqual(
      valuesFromControl(),
      selectedValues
    )

    if (!applyingControlState && inputReplacesSingleSelection(intendedInput)) {
      const clearSelectionForDraft = (): void => {
        singleDraftClearedSelection = true
        writeCanonicalValues([], true)
        combobox.value = null
        restoreControlSelection('draft')
      }

      if (controlMatchesCanonicalSelection) {
        clearSelectionForDraft()
      } else {
        // A WA option click emits input followed by change in the same turn.
        // Defer ambiguous SSR drafts so that real selections win first.
        queueMicrotask(() => {
          if (active && connected && revision === inputRevision) {
            clearSelectionForDraft()
          }
        })
      }
    }

    if (!configuration.dataLoadEvent) {
      if (clearHidePromise && combobox.allowCreate) {
        combobox.allowCreate = false
      }
      openIntentRevision += 1
      wantsOpen = true
      refreshNativeFiltering()
      synchronizeStaticEmptyState()
      schedulePopupOpen()
      return
    }

    combobox.allowCreate = false
    const filter = intendedInput
    pendingFilterIntent = filter
    openIntentRevision += 1
    wantsOpen = true
    queueMicrotask(() => {
      if (active && revision === inputRevision) {
        pendingFilterIntent = undefined
        requestFilter(filter)
      }
    })
  }

  // Primes remote data on focus without opening ahead of a pointer gesture.
  const handleFocus: EventListener = (): void => {
    if (
      configuration.dataLoadEvent &&
      !clearing &&
      !server.disabled &&
      !server.readonly &&
      remoteState.status === 'not-asked'
    ) {
      remoteLoadingPresentationEnabled = true
      requestFilter('')
    }
  }

  // Accepts lifecycle events from this host, not nested WA components.
  const isComboboxLifecycleEvent = (event: Event): boolean =>
    event.composedPath()[0] === combobox

  // Records pointer intent so delayed remote results can reopen the listbox.
  const handlePointerDown: EventListener = (event): void => {
    inputPopupOpenPending = false
    const singleInputGesture = prepareNativeMouseSelection(event)

    if (
      (activeTooltipTag || eventTag(event)) &&
      event instanceof window.PointerEvent
    ) {
      blockedTagTooltipPointer = [event.clientX, event.clientY]
    }
    hideTagTooltip()

    if (comesFromClearButton(event)) {
      clearing = true
      return
    }

    if (server.disabled || server.readonly || !connected) {
      return
    }

    if (combobox.open) {
      if (singleInputGesture) {
        wantsOpen = true
        dismissalRequested = false
        return
      }
      dismissalRequested = !comesFromOption(event)
      return
    }

    dismissalRequested = false
    remoteLoadingPresentationEnabled = true
    openIntentRevision += 1
    wantsOpen = true
    inputPopupOpenPending = singleInputGesture && isPrimaryPointerGesture(event)
    if (configuration.dataLoadEvent) {
      if (remoteState.status === 'not-asked') {
        requestFilter('')
      } else if (remotePopupReady() && !inputPopupOpenPending) {
        renderRemoteStatus()
        schedulePopupOpen()
      }
    } else {
      restoreAuxiliaryContentAfterUpdate()
      if (singleInputGesture && !inputPopupOpenPending) {
        schedulePopupOpen()
      }
    }
  }

  // Opens after native caret or range placement completes on pointer release.
  const handlePointerUp: EventListener = (event): void => {
    if (!inputPopupOpenPending) {
      return
    }

    inputPopupOpenPending = false
    const input = currentComboboxInput()
    if (
      !(
        active &&
        connected &&
        !server.disabled &&
        !server.readonly &&
        input &&
        comesFromComboboxInput(event, input) &&
        isPrimaryPointerGesture(event)
      )
    ) {
      return
    }

    if (!configuration.dataLoadEvent || remotePopupReady()) {
      renderRemoteStatus()
      schedulePopupOpen()
    }
  }

  // Cancels pending input-origin open intent when its pointer never completes.
  const handlePointerCancel = (): void => {
    if (inputPopupOpenPending) {
      inputPopupOpenPending = false
      openIntentRevision += 1
      wantsOpen = false
      dismissalRequested = true
    }
  }

  // Reasserts a settled empty open after WA completes its native click toggle.
  const handleClick: EventListener = (event): void => {
    if (
      comesFromClearButton(event) ||
      comesFromOption(event) ||
      !configuration.dataLoadEvent ||
      !wantsOpen ||
      dismissalRequested ||
      !remotePopupReady()
    ) {
      return
    }

    renderRemoteStatus()
    schedulePopupOpen()
  }

  // Tracks popup intent and restores it after WA's initial reconnect settles.
  const handleShow: EventListener = (event): void => {
    if (!isComboboxLifecycleEvent(event)) {
      return
    }

    hideTagTooltip()
    observeDeferredIcons()
    synchronizeStaticEmptyState()
    if (server.readonly || server.disabled || !connected) {
      event.preventDefault()
      combobox.hide().catch(() => undefined)
      return
    }
    if (!remotePopupReady()) {
      rejectedShowPendingHide = true
      event.preventDefault()
      return
    }
    rejectedShowPendingHide = false
    wantsOpen = true
    announcedRenderRevision = undefined
    synchronizeEmptyNavigation()

    if (recoveredOpenIntentRevision !== openIntentRevision) {
      recoveredOpenIntentRevision = openIntentRevision
      schedulePopupOpen(INITIAL_REMOTE_POPUP_DELAY_MS)
    }
  }

  // Opening announces natively; only later remote renders need an explicit update.
  const handleAfterShow: EventListener = (event): void => {
    if (isComboboxLifecycleEvent(event) && combobox.open) {
      announcedRenderRevision = renderRevision
    }
  }

  // Vetoes WA's automatic empty hide while allowing explicit dismissals.
  const handleHide: EventListener = (event): void => {
    if (!isComboboxLifecycleEvent(event)) {
      return
    }

    if (rejectedShowPendingHide) {
      return
    }
    if (
      (emptyStateVisible || pageZeroSearchIsPending()) &&
      wantsOpen &&
      !dismissalRequested &&
      active &&
      connected &&
      !server.disabled &&
      !server.readonly
    ) {
      event.preventDefault()
      return
    }
    renderSelectedPrefix(valuesFromControl()[0])
    hidingOpenIntentRevision = openIntentRevision
    hidingWasDismissal = dismissalRequested
  }

  // Clears an uncommitted draft after an explicit popup dismissal.
  const handleAfterHide: EventListener = (event): void => {
    if (!isComboboxLifecycleEvent(event)) {
      return
    }

    if (rejectedShowPendingHide) {
      rejectedShowPendingHide = false
      return
    }
    const hideMatchesCurrentIntent =
      hidingOpenIntentRevision === openIntentRevision
    const clearDismissedDraft =
      hideMatchesCurrentIntent &&
      hidingWasDismissal &&
      (emptyStateVisible ||
        pageZeroSearchIsPending() ||
        singleDraftClearedSelection ||
        (inputDraft.trim() !== '' &&
          (server.multiple ||
            selectedValues.length === 0 ||
            inputReplacesSingleSelection(inputDraft))))
    hidingWasDismissal = false

    if (!hideMatchesCurrentIntent) {
      return
    }

    wantsOpen = false
    dismissalRequested = false
    remoteLoadingPresentationEnabled = false
    renderRemoteStatus()

    if (!clearDismissedDraft) {
      return
    }

    inputRevision += 1
    inputDraft = ''
    combobox.inputValue = ''
    singleDraftClearedSelection = false
    emptyStateVisible = false

    restoreControlSelection('draft')
    if (!configuration.dataLoadEvent) {
      refreshNativeFiltering()
      synchronizeStaticEmptyState()
      return
    }
    remoteState = replaceRemoteBootstrapOptions(
      remoteState,
      remoteOptions(remoteState)
    )
    requestFilter('', true)
  }

  // Marks Escape as an explicit dismissal before WA handles it internally.
  const handleDismissalKeydown: EventListener = (event): void => {
    if (!(event instanceof window.KeyboardEvent) || event.key !== 'Escape') {
      return
    }
    if (activeTagTooltip) {
      event.preventDefault()
      event.stopImmediatePropagation()
      hideTagTooltip()
      return
    }
    if (combobox.open) {
      dismissalRequested = true
    }
  }

  // Marks pointer and focus transitions outside the popup as dismissals.
  const handleDocumentDismissal: EventListener = (event): void => {
    if (combobox.open && !event.composedPath().includes(combobox)) {
      dismissalRequested = true
    }
  }

  // Relays Escape past a closed WA field so the next dismissible layer can close.
  const handleClosedEscape: EventListener = (event): void => {
    if (
      event instanceof window.KeyboardEvent &&
      event.key === 'Escape' &&
      !combobox.open
    ) {
      window.document.dispatchEvent(
        new window.KeyboardEvent('keydown', {
          bubbles: true,
          cancelable: true,
          composed: true,
          key: 'Escape'
        })
      )
    }
  }

  // Prevents readonly key gestures while retaining focus and copy access.
  const handleReadonlyKeydown: EventListener = (event): void => {
    if (
      server.readonly &&
      event instanceof window.KeyboardEvent &&
      !isUnmodifiedCopyShortcut(event) &&
      !['Tab', 'Shift', 'Control', 'Meta', 'Alt', 'Escape'].includes(event.key)
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // Handles server-authorized creation through WA's native create event.
  const handleCreate: EventListener = (event): void => {
    event.preventDefault()

    const detail: unknown = hasProp(event, 'detail') ? event.detail : undefined
    const value =
      isPlainObject(detail) && isString(detail.inputValue)
        ? detail.inputValue.trim()
        : ''
    const createEvent = configuration.createEvent

    if (
      !createEvent ||
      value === '' ||
      creating ||
      remoteState.status === 'loading' ||
      server.readonly ||
      server.disabled ||
      !connected
    ) {
      return
    }

    creationFailure = undefined
    creating = true
    const revision = ++creationRevision
    renderRemoteStatus()
    pushEventWithCallback(
      hook,
      createEvent,
      { value },
      configuration.target,
      DEFAULT_PUSH_EVENT_TIMEOUT_MS,
      connectionAbortController.signal
    )
      .then((reply) => {
        if (!(active && connected && revision === creationRevision)) {
          return
        }

        if (configuration.createInDrawer) {
          inputRevision += 1
          pendingFilterIntent = undefined
          inputDraft = ''
          singleDraftClearedSelection = false
          wantsOpen = false
          dismissalRequested = false
          remoteLoadingPresentationEnabled = false
          emptyStateVisible = false
          combobox.inputValue = ''
          refreshNativeFiltering()
          restoreControlSelection('draft')
          combobox.hide().catch(() => undefined)
          return
        }

        const created = parseComboboxOptions([reply])?.[0]
        if (!created) {
          throw new TypeError('Invalid combobox creation response')
        }

        combobox.inputValue = server.multiple ? '' : created.label

        if (configuration.dataLoadEvent) {
          remoteState = addRemoteOption(remoteState, created)
        } else {
          staticOptions = uniqueBy(
            [...staticOptions, created],
            (option) => option.value
          )
          server = { ...server, options: staticOptions }
        }

        renderOptions()
        const values = server.multiple
          ? uniqueBy([...selectedValues, created.value], (item) => item)
          : [created.value]
        singleDraftClearedSelection = false
        writeCanonicalValues(values, true)
        restoreControlSelection()
        if (!server.multiple) {
          wantsOpen = false
          combobox.hide().catch(() => undefined)
        }
      })
      .catch((error) => {
        if (
          !(active && connected && revision === creationRevision) ||
          isAbortError(error)
        ) {
          return
        }
        creationFailure = configuration.failureMessage
        captureFailure(error, 'custom-value-creation', root, {
          event: createEvent,
          value
        })
      })
      .finally(() => {
        if (revision !== creationRevision) {
          return
        }
        creating = false
        if (active) {
          renderRemoteStatus()
        }
      })
  }

  // Closes the popup, commits an empty draft, and refreshes after WA hides.
  const handleClear: EventListener = (): void => {
    clearing = true
    queueMicrotask(() => {
      clearing = false
    })
    inputRevision += 1
    pendingFilterIntent = undefined
    if (!(active && connected) || server.disabled || server.readonly) {
      restoreControlSelection()
      return
    }

    singleDraftClearedSelection = false
    wantsOpen = false
    dismissalRequested = false
    remoteLoadingPresentationEnabled = false
    if (openTimer !== undefined) {
      clearTimeout(openTimer)
      openTimer = undefined
    }
    const popupHidden = combobox.hide().catch(() => undefined)
    clearHidePromise = popupHidden
    popupHidden.then(() => {
      if (clearHidePromise === popupHidden) {
        clearHidePromise = undefined
      }
    })
    inputDraft = ''
    combobox.inputValue = ''
    writeCanonicalValues([], true)
    if (configuration.dataLoadEvent) {
      remoteState = replaceRemoteBootstrapOptions(
        remoteState,
        remoteOptions(remoteState)
      )
      observer?.disconnect()
      renderRemoteStatus()
    }
    queueMicrotask(() => {
      const revision = inputRevision
      popupHidden.then(() => {
        if (
          active &&
          connected &&
          revision === inputRevision &&
          selectedValues.length === 0 &&
          readInputValue() === ''
        ) {
          if (configuration.dataLoadEvent) {
            requestFilter('', true)
          } else {
            refreshNativeFiltering()
          }
        }
      })
    })
  }

  if (configuration.dataLoadEvent) {
    combobox.filter = (option, filter) => {
      if (!remoteOptionsAreCurrent(remoteState, filter)) {
        return combobox.open && remoteState.status !== 'empty'
      }

      switch (remoteState.status) {
        case 'ready':
        case 'loading': {
          const value = option.dataset.value
          return value !== undefined && remoteState.loadedValues.includes(value)
        }
        case 'failure':
          return true
        case 'empty':
        case 'not-asked':
          return false
      }
    }
  } else {
    combobox.filter = (option, filter) => {
      if (clearHidePromise) {
        return false
      }

      const selectedValue = selectedValues[0]
      const selectedOption = selectedValue
        ? optionForValue(selectedValue)
        : undefined
      if (
        !(server.multiple || singleDraftClearedSelection) &&
        selectedOption?.label === filter
      ) {
        return true
      }

      return option.label
        .toLocaleLowerCase()
        .includes(filter.toLocaleLowerCase())
    }
  }

  if (typeof window.IntersectionObserver === 'function') {
    observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        requestNextPage()
      }
    })
    iconObserver = new window.IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            const icons = entry.target.matches('wa-icon[data-deferred-src]')
              ? [entry.target]
              : Array.from(
                  entry.target.querySelectorAll<HTMLElement>(
                    'wa-icon[data-deferred-src]'
                  )
                )
            for (const icon of icons) {
              loadDeferredIcon(icon)
            }
            iconObserver?.unobserve(entry.target)
          }
        }
      },
      {
        root: listboxElement() ?? null,
        rootMargin: `${OPTION_ICON_PRELOAD_MARGIN_PX}px 0px`
      }
    )
  }
  Promise.resolve(combobox.updateComplete)
    .then(() => {
      const anchor =
        combobox.shadowRoot?.querySelector<HTMLElement>('[part~="combobox"]')
      if (active && anchor) {
        anchorSlotCleanup = isolateAnchorSlotChanges(anchor)
      }
    })
    .catch((error) => captureFailure(error, 'anchor-slot-isolation', root))
  Promise.resolve(combobox.updateComplete)
    .then(() => {
      if (active) {
        setupDeferredIconScroll()
        if (combobox.open) {
          scheduleApproachingDeferredIcons()
        }
      }
    })
    .catch((error) => captureFailure(error, 'icon-scroll-listener', root))
  if (typeof window.ResizeObserver === 'function') {
    tagResizeObserver = new window.ResizeObserver(requestTagLayoutFit)
    tagResizeObserver.observe(combobox)
    Promise.resolve(combobox.updateComplete)
      .then(() => {
        const tags = combobox.shadowRoot?.querySelector('[part~="tags"]')
        if (active && connected && tags) {
          tagResizeObserver?.observe(tags)
        }
      })
      .catch((error) => captureFailure(error, 'tag-layout-observer', root))
  }
  const cleanupLiveProps = bindLiveProps(root, {
    read: (propertyNames) => readLiveValues(propertyNames),
    apply: (values) => applyLiveValues(values)
  })
  const cleanupSlots = composeCleanup(
    ...[clearIcon, ...emptyStatusSlots].map(preserveSlotVisibility)
  )
  const cleanupSelectedPrefix = preserveSelectedPrefixVisibility(prefix)
  const cleanupListeners = composeCleanup(
    listen(combobox, 'change', handleChange),
    listen(combobox, 'input', handleInputIntent, true),
    listen(combobox, 'input', handleInput),
    listen(combobox, 'focus', handleFocus),
    listen(combobox, 'pointerdown', handlePointerDown),
    listen(combobox, 'click', handleClick),
    listen(combobox, 'pointermove', handleTagPointerMove),
    listen(combobox, 'pointerleave', handleTagPointerLeave),
    listen(combobox, 'wa-show', handleShow),
    listen(combobox, 'wa-after-show', handleAfterShow),
    listen(combobox, 'wa-hide', handleHide),
    listen(combobox, 'wa-after-hide', handleAfterHide),
    listen(combobox, 'wa-create', handleCreate),
    listen(combobox, 'wa-clear', handleClear),
    listen(combobox, 'keydown', handleDismissalKeydown, true),
    listen(combobox, 'keydown', handleClosedEscape, true),
    listen(combobox, 'keydown', handleReadonlyKeydown, true),
    listen(combobox, 'pointerup', handlePointerUp),
    listen(combobox, 'pointercancel', handlePointerCancel),
    listen(window.document, 'mousedown', handleDocumentDismissal, true),
    listen(window.document, 'focusin', handleDocumentDismissal, true),
    cleanupInputSelection,
    cleanupSlots,
    cleanupSelectedPrefix,
    cleanupLiveProps
  )

  if (adoptRenderedOptions()) {
    renderSelectedPrefix()
    observeDeferredIcons()
    restoreControlSelection('empty-draft')
  } else {
    renderOptions()
  }
  writeCarrier(carrier, selectedValues, server.disabled, configuration.target)
  applyServerPresentation()
  requestTagLayoutFit()

  return {
    cleanup: () => {
      active = false
      connected = false
      inputPopupOpenPending = false
      tagFitRevision += 1
      remoteLoadingPresentationEnabled = false
      pendingFilterIntent = undefined
      hideTagTooltip()
      creationRevision += 1
      creating = false
      connectionAbortController.abort()
      controlWriteRevision += 1
      applyingControlState = false
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer)
      }
      if (formTimer !== undefined) {
        clearTimeout(formTimer)
      }
      if (loadingPresentationTimer !== undefined) {
        clearTimeout(loadingPresentationTimer)
      }
      if (openTimer !== undefined) {
        clearTimeout(openTimer)
      }
      observer?.disconnect()
      iconObserver?.disconnect()
      iconScrollCleanup()
      anchorSlotCleanup()
      if (iconScrollFrame !== undefined) {
        window.cancelAnimationFrame(iconScrollFrame)
        iconScrollFrame = undefined
      }
      tagResizeObserver?.disconnect()
      lastTagLayoutFingerprint = undefined
      tagFitRequested = false
      cleanupListeners()
      for (const slot of emptyStatusSlots) {
        slot.remove()
      }
      tokenByValue.clear()
      valueByToken.clear()
      combobox.hide().catch(() => undefined)
    },
    disconnected: () => {
      connected = false
      inputPopupOpenPending = false
      clearNativeMouseFocusIntent()
      tagFitRevision += 1
      tagFitRequested = false
      remoteLoadingPresentationEnabled = false
      pendingFilterIntent = undefined
      hideTagTooltip()
      creationRevision += 1
      creating = false
      creationFailure = undefined
      connectionAbortController.abort()
      if (iconScrollFrame !== undefined) {
        window.cancelAnimationFrame(iconScrollFrame)
        iconScrollFrame = undefined
      }
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer)
        debounceTimer = undefined
      }
      if (openTimer !== undefined) {
        clearTimeout(openTimer)
        openTimer = undefined
      }
      remoteState = replaceRemoteBootstrapOptions(
        remoteState,
        remoteOptions(remoteState)
      )
      combobox.disabled = true
      renderRemoteStatus()
    },
    reconnected: () => {
      if (connectionAbortController.signal.aborted) {
        connectionAbortController = new AbortController()
      }
      connected = true
      setupDeferredIconScroll()
      if (combobox.open) {
        scheduleApproachingDeferredIcons()
      }
      applyServerPresentation()
      requestTagLayoutFit()
    },
    sync: () => {
      applyServerPresentation()
      restoreControlSelection('empty-draft')
      requestTagLayoutFit()
    }
  }
}

// Public functions ------------------------------------------------------------

// Creates one controller and captures setup defects at the component boundary.
export const createComboboxController = (
  root: HTMLElement,
  hook: ControllerHook
): ComboboxController | undefined => setupCombobox(root, hook)
